import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Drawer from '~/components/Drawer';
import { labelStyles, inputStyles, requiredStyles } from './styles';
import { putCampaignRequestReferredBy } from '~/services/campaign';
import { apiUrl, authHeaders } from '~/services/auth';
import { createMedicalCenter } from '~/services/medical-centers';
import ReferringProfessionalField, {
  EMPTY_REFERRING_PROFESSIONAL,
  REFERRING_PROFESSIONAL_REFERRAL_CODES,
  ReferringProfessional,
  isReferringProfessionalMissing,
  referringProfessionalFromRequest,
  referringProfessionalPayload,
} from '~/components/CampaignRequestDetails/ReferringProfessionalField';

const OTHER_PROF_CODE = 'other_prof';
// Referral sources ("Referred By") for which the Medical Center picker is
// shown and its selection is saved. Matched on ReferredBySource.code.
const MEDICAL_CENTER_REFERRAL_CODES = [
  'med_center',
  'case_manager',
  'financial',
  'medical_professional',
  'social_worker',
  'tx_coord',
];

interface SelectOption {
  value: number | string;
  label: string;
  code?: string;
}

const US_STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District Of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

// A medical center's location is a state's full name or 'Treatment Abroad'.
const stateOptions: SelectOption[] = [
  ...Object.entries(US_STATES).map(([value, label]) => ({ value, label })),
  { value: 'XX', label: 'Treatment Abroad' },
];

const searchMedicalCenters = async (state: string) => {
  const headers = authHeaders();
  const url = `${apiUrl}/v2/searchMedicalCenters`;
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ state }),
  });
  if (!resp.ok) throw new Error('Failed to search medical centers');
  return await resp.json();
};

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  campaignRequest?: any;
  metadata?: any;
  onCampaignRequestUpdate?: (updatedCampaignRequest: any) => void;
}

const ReferredByDrawer: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  campaignRequest,
  metadata,
  onCampaignRequestUpdate,
}) => {
  const [selectedSource, setSelectedSource] = useState<SelectOption | null>(
    null,
  );
  const [otherText, setOtherText] = useState('');
  const [selectedState, setSelectedState] = useState<SelectOption | null>(null);
  const [selectedMedicalCenter, setSelectedMedicalCenter] =
    useState<SelectOption | null>(null);
  const [medicalCenterOptions, setMedicalCenterOptions] = useState<
    SelectOption[]
  >([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [showAddCenterForm, setShowAddCenterForm] = useState(false);
  const [newCenterName, setNewCenterName] = useState('');
  const [isCreatingCenter, setIsCreatingCenter] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [referringProfessional, setReferringProfessional] =
    useState<ReferringProfessional>(EMPTY_REFERRING_PROFESSIONAL);

  const heardOfOptions: SelectOption[] = useMemo(
    () =>
      (metadata?.heard_of_sources || []).map((item: any) => ({
        value: item.id,
        label: item.name,
        code: item.code,
      })),
    [metadata],
  );

  const isOtherSelected = selectedSource?.code === OTHER_PROF_CODE;
  const isMedCenterSelected = MEDICAL_CENTER_REFERRAL_CODES.includes(
    selectedSource?.code ?? '',
  );
  const isMedCenterMissing = isMedCenterSelected && !selectedMedicalCenter;
  const isReferringSourceSelected =
    REFERRING_PROFESSIONAL_REFERRAL_CODES.includes(selectedSource?.code ?? '');
  const isReferrerMissing =
    isReferringSourceSelected &&
    isReferringProfessionalMissing(referringProfessional);

  const loadMedicalCenters = async (
    state: string,
    preselectedMedCenterId?: number,
  ) => {
    setLoadingCenters(true);
    try {
      const centers = await searchMedicalCenters(state);
      const opts: SelectOption[] = centers.map((center: any) => ({
        label: center.name,
        value: center.id,
      }));
      setMedicalCenterOptions(opts);
      if (preselectedMedCenterId) {
        const selectedOpt = opts.find(
          (opt) => opt.value === preselectedMedCenterId,
        );
        if (selectedOpt) setSelectedMedicalCenter(selectedOpt);
      }
    } catch (err) {
      console.error('Error loading medical centers:', err);
    } finally {
      setLoadingCenters(false);
    }
  };

  // Reset the form from the campaignRequest whenever the drawer opens
  useEffect(() => {
    if (!isOpen || !campaignRequest) return;

    setError(null);
    setSubmitAttempted(false);
    setShowAddCenterForm(false);
    setNewCenterName('');
    setSelectedSource(
      campaignRequest?.heard_of_source
        ? {
            value: campaignRequest.heard_of_source.id,
            label: campaignRequest.heard_of_source.name,
            code: campaignRequest.heard_of_source.code,
          }
        : null,
    );
    setOtherText(campaignRequest?.heard_of_source_other || '');
    setReferringProfessional(referringProfessionalFromRequest(campaignRequest));

    // Select the saved center even when its location isn't in the list,
    // since saving without one clears it from the request.
    if (
      MEDICAL_CENTER_REFERRAL_CODES.includes(
        campaignRequest?.heard_of_source?.code ?? '',
      ) &&
      campaignRequest?.medical_center
    ) {
      const medCenter = campaignRequest.medical_center;
      setSelectedMedicalCenter({ value: medCenter.id, label: medCenter.name });
      if (medCenter.location) {
        setSelectedState(
          stateOptions.find((opt) => opt.label === medCenter.location) ?? {
            value: medCenter.location,
            label: medCenter.location,
          },
        );
        loadMedicalCenters(medCenter.location, medCenter.id);
      }
    } else {
      setSelectedState(null);
      setSelectedMedicalCenter(null);
      setMedicalCenterOptions([]);
    }
  }, [isOpen, campaignRequest]);

  // Clear medical center selection when source moves away from med_center
  useEffect(() => {
    if (!isMedCenterSelected) {
      setSelectedState(null);
      setSelectedMedicalCenter(null);
      setMedicalCenterOptions([]);
      setShowAddCenterForm(false);
      setNewCenterName('');
      setReferringProfessional(EMPTY_REFERRING_PROFESSIONAL);
    }
  }, [isMedCenterSelected]);

  // A picked person belongs to the old center; a typed name doesn't
  const clearPickedProfessional = () =>
    setReferringProfessional((prev) =>
      prev.isNew ? prev : EMPTY_REFERRING_PROFESSIONAL,
    );

  const handleStateChange = (option: SelectOption | null) => {
    setSelectedState(option);
    setSelectedMedicalCenter(null);
    clearPickedProfessional();
    setMedicalCenterOptions([]);
    setShowAddCenterForm(false);
    setNewCenterName('');
    if (option) loadMedicalCenters(option.label);
  };

  const handleAddCenter = async () => {
    if (!newCenterName.trim() || !selectedState) return;
    setIsCreatingCenter(true);
    try {
      const center = await createMedicalCenter({
        name: newCenterName.trim(),
        location: selectedState.label,
      });
      const newOption: SelectOption = { value: center.id, label: center.name };
      setMedicalCenterOptions((prev) => [...prev, newOption]);
      setSelectedMedicalCenter(newOption);
      clearPickedProfessional();
      setShowAddCenterForm(false);
      setNewCenterName('');
    } catch (err) {
      console.error('Error creating medical center:', err);
    } finally {
      setIsCreatingCenter(false);
    }
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    if (isMedCenterMissing || isReferrerMissing) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const payload: any = {
        heard_of_source_id: selectedSource?.value ?? null,
        heard_of_source_other: isOtherSelected ? otherText || '' : '',
      };
      if (isMedCenterSelected) {
        payload.medical_center_id = selectedMedicalCenter?.value ?? null;
      }
      if (isReferringSourceSelected) {
        Object.assign(
          payload,
          referringProfessionalPayload(referringProfessional),
        );
      }
      const response = await putCampaignRequestReferredBy(
        campaignRequest?.id,
        payload,
      );
      onCampaignRequestUpdate?.(response?.campaign_request ?? response);
      setIsOpen(false);
    } catch (err: any) {
      console.error('Error updating referred by source:', err);
      setError(err?.detail || err?.message || 'Failed to update referred by');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Edit Referred By'>
      <label htmlFor='rb-source' className={`${labelStyles} w-full`}>
        Referred By
      </label>
      <Select
        inputId='rb-source'
        className='w-full'
        menuPlacement='auto'
        options={heardOfOptions}
        value={selectedSource}
        onChange={(opt) => setSelectedSource(opt as SelectOption | null)}
        isClearable
      />

      {isOtherSelected && (
        <>
          <label htmlFor='rb-other' className={`${labelStyles} w-full`}>
            Please specify
          </label>
          <input
            id='rb-other'
            type='text'
            className={`${inputStyles} w-full`}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
          />
        </>
      )}

      {isMedCenterSelected && (
        <>
          <label htmlFor='rb-state' className={`${labelStyles} w-full`}>
            Medical Center State
            <span className={requiredStyles}>*</span>
          </label>
          <Select
            inputId='rb-state'
            className='w-full'
            menuPlacement='auto'
            options={stateOptions}
            value={selectedState}
            onChange={(opt) => handleStateChange(opt as SelectOption | null)}
            isClearable
          />

          <label htmlFor='rb-medcenter' className={`${labelStyles} w-full`}>
            Medical Center
            <span className={requiredStyles}>*</span>
          </label>
          <Select
            inputId='rb-medcenter'
            className='w-full'
            menuPlacement='auto'
            options={medicalCenterOptions}
            value={selectedMedicalCenter}
            onChange={(opt) => {
              if (opt?.value !== selectedMedicalCenter?.value) {
                clearPickedProfessional();
              }
              setSelectedMedicalCenter(opt as SelectOption | null);
            }}
            isDisabled={!selectedState || loadingCenters}
            isLoading={loadingCenters}
            isClearable
          />
          {submitAttempted && isMedCenterMissing && (
            <div className='mt-[4px] text-sm text-[#ff4b4b]'>
              This field is required
            </div>
          )}

          {selectedState &&
            (showAddCenterForm ? (
              <div className='mt-[12px] flex gap-[8px]'>
                <input
                  type='text'
                  className={`${inputStyles} grow`}
                  placeholder='New medical center name'
                  value={newCenterName}
                  onChange={(e) => setNewCenterName(e.target.value)}
                />
                <button
                  type='button'
                  className='rounded-full border-[2px] px-[16px] py-[7px] text-[12px] font-bold uppercase disabled:opacity-50'
                  onClick={handleAddCenter}
                  disabled={isCreatingCenter || !newCenterName.trim()}
                >
                  {isCreatingCenter ? 'Adding…' : 'Add'}
                </button>
              </div>
            ) : (
              <button
                type='button'
                className='mt-[8px] text-sm text-primary hover:underline'
                onClick={() => setShowAddCenterForm(true)}
              >
                + Add a medical center
              </button>
            ))}

          {isReferringSourceSelected && selectedMedicalCenter && (
            <ReferringProfessionalField
              id='rb-referring-professional'
              medicalCenterId={selectedMedicalCenter.value as number}
              value={referringProfessional}
              onChange={setReferringProfessional}
              showError={submitAttempted && isReferrerMissing}
              disabled={isSubmitting}
              labelClassName={`${labelStyles} w-full`}
              inputClassName={`${inputStyles} w-full`}
              requiredClassName={requiredStyles}
            />
          )}
        </>
      )}

      {error && <div className='mt-[12px] text-sm text-[#ff4b4b]'>{error}</div>}

      <div className='mb-[30px] flex gap-[16px]'>
        <button
          className='mt-[30px] grow rounded-full border-[2px] border-black px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:bg-black hover:text-white'
          onClick={() => setIsOpen(false)}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:opacity-50'
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Drawer>
  );
};

export default ReferredByDrawer;
